// Shared bits for the Soulumo creation flow: Explain → Agree → Record → Create.
import React from "react";
import axios from "axios";
import { TOK, SERIF, SANS } from "../../pages/Soulumo/SignatureBits";
import { openPaddleIfActive } from "../voiceClone/paddleCheckout";

export const API = process.env.REACT_APP_BACKEND_URL;
export const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` } });
export { TOK, SERIF, SANS };

export const SECTION_ORDER = ["truth", "nothing_missing", "trust", "identity", "performance", "final_loop"];
const BAND_WORDS = { delta: "Deep rest", theta: "Grounding", alpha: "Calm", beta: "Clear focus", gamma: "Bright" };

// Soulumo decides the length: longer for sleep, otherwise 10 minutes.
export const decideDuration = (text) => (/\b(sleep|insomnia|night|bed ?time)\b/i.test(text || "") ? 20 : 10);

export const journeyLabel = (plan, minutes) => {
  const words = [];
  (plan?.binaural_stages || []).forEach((b) => {
    const w = BAND_WORDS[typeof b === "string" ? b : b?.band];
    if (w && !words.includes(w)) words.push(w);
  });
  return [...words.slice(0, 2), `${minutes} minutes`].join(" · ");
};

export const flattenLines = (sections) =>
  (sections || []).flatMap((s) => (s.lines || []).map((l) => l.text).filter(Boolean));

export const scriptFromSections = (sections) => {
  const out = {};
  (sections || []).forEach((s) => {
    const lines = (s.lines || []).filter((l) => (l.text || "").trim()).map((l) => ({ text: l.text.trim() }));
    if (lines.length) out[s.section] = lines;
  });
  return out;
};

export const startCloneCheckout = async () => {
  if (await openPaddleIfActive({ plan: "monthly", source: "create_flow" })) return;
  const r = await axios.post(`${API}/api/voice-clone/checkout`, { origin_url: window.location.origin }, auth());
  if (r.data?.checkout_url) window.location.href = r.data.checkout_url;
};

export const StepDots = ({ step }) => {
  const labels = ["Explain", "Agree", "Record", "Create"];
  return (
    <div className="flex items-center gap-2" data-testid="simple-create-steps">
      {labels.map((l, i) => (
        <div key={l} className="flex items-center gap-2">
          <span className="h-1.5 rounded-full transition-[width,background-color] duration-500"
            style={{ width: i === step ? 22 : 6, background: i <= step ? TOK.gold : TOK.goldMist }} />
        </div>
      ))}
      <span className="ml-2 text-[0.6rem] uppercase tracking-[0.28em]" style={{ color: TOK.goldSoft, fontFamily: SANS }}>
        {labels[step]}
      </span>
    </div>
  );
};

export const PrimaryBtn = ({ children, testId, ...p }) => (
  <button type="button" data-testid={testId} {...p}
    className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 transition-[opacity,transform] duration-200 active:scale-[0.98] disabled:opacity-40"
    style={{ minHeight: 58, background: TOK.gold, color: TOK.ink, fontFamily: SANS, fontSize: "0.74rem",
      letterSpacing: "0.26em", textTransform: "uppercase", fontWeight: 700, boxShadow: "0 12px 34px rgba(215,187,122,0.22)" }}>
    {children}
  </button>
);

export const QuietLink = ({ children, testId, ...p }) => (
  <button type="button" data-testid={testId} {...p}
    className="mx-auto block text-sm transition-opacity duration-200 hover:opacity-100"
    style={{ color: TOK.creamMist, fontFamily: SERIF, fontStyle: "italic", opacity: 0.85 }}>
    {children}
  </button>
);

export const Title = ({ children }) => (
  <h2 className="text-3xl sm:text-4xl font-light leading-tight" style={{ fontFamily: SERIF, color: TOK.cream }}>
    {children}
  </h2>
);
